import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", image: "", description: "", country: "", region: "International", featured: false, active: true };
const input = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brandorange";
const label = "text-xs font-bold uppercase tracking-wide text-gray-400";

export default function AdminDestinations() {
  const [items, setItems] = useState(null);
  const [regionTab, setRegionTab] = useState("India");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => api.get("/admin/destinations").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleActive = async (d) => {
    try { await api.put(`/admin/destinations/${d.id}`, { ...d, active: d.active === false }); load(); }
    catch (e) { toast.error(apiError(e, "Could not update")); }
  };

  const genAi = async () => {
    if (!form.name) { toast.error("Enter a destination name first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/ai/destination", { name: form.name });
      setForm((f) => ({
        ...f,
        description: data.description || data.overview || f.description,
        country: data.country || f.country,
        region: data.region || f.region,
      }));
      toast.success("AI content generated!");
    } catch (e) { toast.error(apiError(e)); } finally { setAiLoading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/admin/destinations/${editId}`, form);
      else await api.post("/admin/destinations", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/destinations/${id}`); toast.success("Deleted"); load(); };

  return (
    <AdminLayout title="Destinations">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 rounded-full bg-gray-100 p-1" data-testid="dest-region-tabs">
          {["India", "International"].map((r) => (
            <button key={r} onClick={() => setRegionTab(r)} data-testid={`dest-region-${r}`} className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${regionTab === r ? "brand-gradient text-white" : "text-navy hover:bg-white"}`}>{r} <span className="opacity-70">({(items || []).filter((d) => (d.region || "International") === r).length})</span></button>
          ))}
        </div>
        <button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-destination-btn" className="btn-gradient flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold"><Plus className="h-4 w-4" /> New Destination</button>
      </div>
      {items === null ? <div className="grid gap-4 sm:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 skeleton rounded-2xl" />)}</div> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.filter((d) => (d.region || "International") === regionTab).map((d) => (
            <div key={d.id} data-testid={`admin-dest-${d.slug}`} className={`overflow-hidden rounded-2xl border border-gray-100 bg-white card-soft ${d.active === false ? "opacity-60" : ""}`}>
              <img src={d.image} alt={d.name} className="h-32 w-full object-cover" onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80"; }} />
              <div className="p-4"><p className="font-display font-bold text-navy">{d.name}</p><p className="text-xs text-gray-400">{d.region} · {d.country}</p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex gap-2"><button onClick={() => { setForm({ ...EMPTY, ...d }); setEditId(d.id); setOpen(true); }} data-testid={`edit-dest-${d.slug}`} className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"><Pencil className="h-4 w-4" /></button><button onClick={() => del(d.id)} className="rounded-lg p-2 text-brandred hover:bg-red-50"><Trash2 className="h-4 w-4" /></button></div>
                  <button onClick={() => toggleActive(d)} data-testid={`toggle-dest-${d.slug}`} className="flex items-center gap-2 text-xs font-bold text-navy" title={d.active === false ? "Hidden from website" : "Visible on website"}>
                    {d.active === false ? "OFF" : "ON"}
                    <span className={`flex h-5 w-9 items-center rounded-full p-0.5 transition-colors ${d.active === false ? "bg-gray-300" : "bg-emerald-500"}`}><span className={`h-4 w-4 rounded-full bg-white transition-transform ${d.active === false ? "" : "translate-x-4"}`} /></span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="destination-form-dialog"><DialogHeader><DialogTitle className="font-display text-2xl text-navy">{editId ? "Edit" : "New"} Destination</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1"><span className={label}>Name</span><input data-testid="dest-name" className={`${input} mt-1`} placeholder="e.g. Sikkim" value={form.name} onChange={set("name")} /></div>
              <button type="button" onClick={genAi} disabled={aiLoading} data-testid="ai-dest-btn" className="flex items-center gap-1.5 rounded-xl border border-brandorange px-3 py-2.5 text-xs font-bold text-brandorange hover:bg-orange-50 disabled:opacity-60">{aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI Generate</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={label}>Country</span><input className={`${input} mt-1`} placeholder="Country" value={form.country} onChange={set("country")} /></div>
              <div><span className={label}>Region</span><select data-testid="dest-region" className={`${input} mt-1`} value={form.region} onChange={set("region")}><option value="India">India</option><option value="International">International</option></select></div>
            </div>
            <div><span className={label}>Image URL</span><input data-testid="dest-image" className={`${input} mt-1`} placeholder="Image URL" value={form.image} onChange={set("image")} /></div>
            <div><span className={label}>Description</span><textarea rows={4} data-testid="dest-desc" className={`${input} mt-1`} placeholder="Description" value={form.description} onChange={set("description")} /></div>
            <label className="flex items-center gap-2 text-sm font-semibold text-navy"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
            <button data-testid="save-dest-btn" disabled={saving} className="btn-gradient flex w-full items-center justify-center rounded-full py-3 font-bold disabled:opacity-70">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
